import React from "react";
import PropTypes from "prop-types";
import {makeStyles} from "@material-ui/styles";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardActions,
  Button, Tooltip
} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {StarBorder} from "@material-ui/icons";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[200]
  },
  smallFont: {
    fontSize: "12px",
  },
  promoFont: {
    fontSize: '16px'
  },
  cardPricing: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexDirection: "column"
  },
  center: {
    textAlign: "center"
  },
  cardContent: {
    flexGrow: 1,
  },
  bold: {
    fontWeight: 600
  },
  cardFeatures: {
    "& ul": {
      paddingInlineStart: 0,
      listStyleType: "none",
    }
  }
}));


const PlanCard = ({plan, withAction, submitting, ctaType = 'trial', currentPlanId = null, warning, warningIf}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const features = JSON.parse(plan.Features);
  const tags = JSON.parse(plan.Tags);
  const isPopular = tags.findIndex(e => e === 'popular') !== -1;
  const isPromo = tags.findIndex(e => e === 'promo') !== -1;

  const getRenewalPeriod = (period, unit) => {
    switch (unit) {
      case 3:
        return (
          <Typography component="span" color="textSecondary" className={classes.smallFont}>
            {t(`per${period > 1 ? period : ''}Month`)}
          </Typography>
        );
      case 4:
        return (
          <Typography component="span" color="textPrimary" className={clsx({
            [classes.smallFont]: !isPromo,
            [classes.bold]: true,
            [classes.promoFont]: isPromo
          })}>
            {period > 10 ? t('lifetime') : t(`per${period > 1 ? period : ''}Year`)}
          </Typography>
        );
      default:
        break;
    }
  }


  return (
    <Card className={classes.root}>
      <CardHeader
        title={t(plan.Title)}
        subheader={t(plan.Subtitle1)}
        titleTypographyProps={{align: "center"}}
        subheaderTypographyProps={{align: "center", className: classes.smallFont}}
        className={classes.cardHeader}
        action={isPopular ? <StarBorder/> : null}
      />
      <CardContent className={classes.cardContent}>
        <div className={classes.cardPricing}>
          <Typography component="h2" variant="h3" color="textPrimary">
            {plan.Currency}
            {plan.Price}
          </Typography>

          {getRenewalPeriod(plan.Period, plan.PeriodUnit)}
          <Typography component="span" color="textSecondary" className={classes.smallFont}>
            {t('vatNotIncluded')}
          </Typography>
        </div>
        <div className={classes.cardFeatures}>
          <ul>
            {features.map((f, index) => (
              <Typography
                key={index}
                component="li"
                variant="subtitle1"
                align="center"
              >
                {t(f)}
              </Typography>
            ))}
          </ul>
        </div>
      </CardContent>
      {withAction ? (
        <CardActions>
          <Tooltip
            title={warningIf ? warning : ""}
          >
            <div style={{width: '100%'}}>
              <Button
                fullWidth
                variant={isPopular ? "contained" : "outlined"}
                color="primary"
                onClick={() => withAction(plan)}
                disabled={(submitting || currentPlanId === plan.Id || warningIf)}
              >
                {currentPlanId === plan.Id ? t('currentPlan') : (ctaType === 'trial' ? t(plan.TrialStartAction) : t(plan.UpgradeAction))}
              </Button>
            </div>
          </Tooltip>
        </CardActions>
      ) : null}
    </Card>
  );
};

PlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  withAction: PropTypes.func,
  submitting: PropTypes.bool
};

export default PlanCard;
